import { collabStateManager } from '@databases/collab-state-manager';
import { useDatabaseContent } from '@databases/database-state';
import { type AsyncOperation, AsyncOperationStatus } from '@databases/redux-types';
import { type DatabaseLayout } from '@databases/types/database/view';
import { useEffect } from 'react';

import {
  getFieldsCount,
  getFieldTypeCounts,
  getFieldTypes,
  getEntriesCount,
  getViewsCount
} from './database-size/resolvers';
import { type DatabaseSizeMetrics } from './database-size/types';
import { reportDatabaseShareProgress } from './report-database-share-progress';
import { useAnalytics } from './useAnalytics';
import { getDatabaseContainerType } from './utils';

const databasesWithCreatedEventFired = new Set<string>();

let databaseSizeMetrics: DatabaseSizeMetrics = {
  contentId: false,
  entriesCount: false,
  fieldsCount: false,
  fieldTypeCounts: false,
  fieldTypes: false,
  viewsCount: false
};

export function getDatabaseSizeMetrics(): DatabaseSizeMetrics {
  return databaseSizeMetrics;
}

function setDatabaseSizeMetrics(metrics: DatabaseSizeMetrics): void {
  databaseSizeMetrics = {
    contentId: metrics.contentId,
    entriesCount: metrics.entriesCount,
    fieldsCount: metrics.fieldsCount,
    fieldTypeCounts: metrics.fieldTypeCounts,
    fieldTypes: metrics.fieldTypes,
    viewsCount: metrics.viewsCount
  };
}

export const useTrackDatabaseMetrics = ({
  databaseStatus,
  viewSettings
}: {
  databaseStatus: AsyncOperation<void> | undefined;
  viewSettings: DatabaseLayout;
}) => {
  const { contentId } = useDatabaseContent();
  const analytics = useAnalytics();

  const manager = collabStateManager.getCollabManager(contentId);

  useEffect(() => {
    if (
      !contentId ||
      databaseStatus?.status !== AsyncOperationStatus.SUCCEEDED ||
      !manager
    ) {
      return;
    }

    const fire = () => {
      const jsonData = manager.database.toJSON();

      void reportDatabaseShareProgress(jsonData);

      const attributes = {
        fieldsCount: getFieldsCount(jsonData),
        fieldTypeCounts: getFieldTypeCounts(jsonData),
        fieldTypes: getFieldTypes(jsonData),
        entriesCount: getEntriesCount(jsonData),
        viewsCount: getViewsCount(jsonData),
        viewSettings: {
          layoutType: viewSettings.type,
          showNumberedEntriesEnabled: viewSettings.showNumberedEntries,
          cardWidth: viewSettings.cardWidth
        }
      };

      setDatabaseSizeMetrics({ ...attributes, contentId });

      const databaseContainer = getDatabaseContainerType();

      analytics.sendDatabaseProfiledEvent({
        contentId,
        databaseContainer,
        attributes
      });

      if (
        manager.wasCreatedThisSession &&
        !databasesWithCreatedEventFired.has(contentId)
      ) {
        databasesWithCreatedEventFired.add(contentId);
        analytics.sendDatabaseCreatedEvent({
          contentId,
          databaseContainer
        });
      }
    };

    fire();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [databaseStatus?.status, contentId, manager]);
};
